// 方案與帳務域 REST 客戶端(S33–S36,IA 定案 specs/01 §2.3;admin#3)。
// 後端契約 = restful /v3/admin/plans* + /v3/admin/plan-grants* +
// /v3/admin/accounting/* + /v3/admin/reconciliations*(全部已在 restful main)。
// 金額一律 minor units(分)+ ISO-4217 currency;時間戳一律 µs since epoch。
// UI 鐵則:不顯示 payment secret / private key;gateway 參照僅顯示 provider id。

import { apiFetch } from '../../lib/api';

// ---- S33 Plan catalog ----

export interface PlanEntitlements {
  // Ops 軸三欄。這裡曾經完全缺席(前端 15 欄 vs 後端 18 欄),所以草稿 payload
  // 連「想送」都送不出去 —— 而後端的 PlanEntitlements 這三欄沒有 omitempty,
  // 缺席即解碼成 false/0/0。由既有 Ops 方案衍生新版本因此會靜默關掉 Ops,
  // publish 後整租戶 /v3/ops fail-closed(admin 00 §3.7 S33)。
  // 非可選:後端自 restful!391 起對缺任一欄回 400。
  ops_enabled: boolean;
  max_seats: number;
  max_devices: number;
  recording_enabled: boolean;
  recording_modes: string[];
  retention_days: number;
  quality_max: string;
  locations: string[];
  storage_gb: number;
  event_retention_days: number;
  relay_gb: number;
  device_count: number;
  camera_count: number;
  channel_count: number;
  share_users: number;
  ai_features: string[];
  ai_monthly_units: number;
  firmware_channels: string[];
}

export interface PlanPrice {
  price_id: string;
  region: string;
  currency: string;
  amount_minor: number;
  billing_interval: string;
}

export interface PlanDefinition {
  plan_id: string;
  version: number;
  status: string; // draft | published | retired
  name: string;
  audience: string; // consumer | business | partner | internal
  family_id?: string;
  billing_interval: string; // day | week | month | year | one_time
  purchasable: boolean;
  effective_from?: number;
  effective_to?: number;
  entitlements: PlanEntitlements;
  prices: PlanPrice[];
  created_by?: string;
  created_at?: number;
  published_by?: string;
  published_at?: number;
  retired_by?: string;
  retired_at?: number;
}

export interface EntitlementExplainStage {
  layer: string;
  status: string;
  code?: string;
  details?: Record<string, unknown>;
}

export interface EffectiveEntitlementExplain {
  subject_type: string;
  subject_id: string;
  decision: 'effective' | 'provisional' | 'denied';
  complete: boolean;
  entitlements: PlanEntitlements;
  commercial: { sources: Array<{ plan_id: string; plan_version: number; source: string; grant_id?: string }>; snapshot_version: string };
  stages: EntitlementExplainStage[];
  snapshot_version: string;
  evaluated_at: number;
}

/** Admin-only server-authoritative AID ∩ Plan/Grant ∩ runtime ∩ authz ∩ policy explain. */
export async function getAdminEffectiveEntitlements(deviceId: string): Promise<EffectiveEntitlementExplain> {
  const res = await apiFetch<EffectiveEntitlementExplain>(`/v3/admin/devices/${encodeURIComponent(deviceId)}/entitlements`);
  return { ...res.data, stages: Array.isArray(res.data?.stages) ? res.data.stages : [] };
}

export type CommerceOperationAction =
  | 'aid.revoke'
  | 'plan.publish'
  | 'plan.retire'
  | 'grant.create'
  | 'grant.revoke'
  | 'credit.create'
  | 'refund.create'
  | 'adjustment.post'
  // AI 側 RTBF(不可逆跨 owner 清除)自 restful!404 起改走本流程,
  // 直打 /v3/admin/ai/rtbf 一律 409 DUAL_CONTROL_REQUIRED。
  | 'ai.rtbf';

export interface CommerceOperation {
  operation_id: string;
  action: CommerceOperationAction;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  maker_id: string;
  checker_id?: string;
  reason: string;
  case_id: string;
  requested_at: number;
  approved_at?: number;
  completed_at?: number;
  expires_at: number;
  failure_code?: string;
  result?: Record<string, unknown>;
}

/** 建立高風險 AID／Plan／Grant／帳務操作；只排入 pending，不直接執行。 */
export async function requestCommerceOperation(
  action: CommerceOperationAction,
  payload: Record<string, unknown>,
  audit: { reason: string; case_id: string }
): Promise<CommerceOperation> {
  const res = await apiFetch<CommerceOperation>('/v3/admin/commerce-operations', {
    method: 'POST',
    body: { action, payload, reason: audit.reason, case_id: audit.case_id },
  });
  return res.data;
}

export async function getCommerceOperation(operationId: string): Promise<CommerceOperation> {
  const res = await apiFetch<CommerceOperation>(`/v3/admin/commerce-operations/${encodeURIComponent(operationId)}`);
  return res.data;
}

/** checker 的密碼只送到 step-up 端點，不寫入操作紀錄。 */
export async function approveCommerceOperation(operationId: string, password: string): Promise<CommerceOperation> {
  const res = await apiFetch<CommerceOperation>(
    `/v3/admin/commerce-operations/${encodeURIComponent(operationId)}/approve`,
    { method: 'POST', body: { password } }
  );
  return res.data;
}

/** GET /v3/admin/plans?status= — status 空值列全部(draft+published+retired)。 */
export async function listPlans(status?: string): Promise<PlanDefinition[]> {
  const res = await apiFetch<PlanDefinition[]>('/v3/admin/plans', { query: { status } });
  return Array.isArray(res.data) ? res.data : [];
}

/** POST /v3/admin/plans — 建立不可變 Plan 版本草稿。reason/case_id 必填(後端擋)。 */
export async function createPlanDraft(plan: PlanDefinition, audit: { reason: string; case_id: string }): Promise<PlanDefinition> {
  const res = await apiFetch<PlanDefinition>('/v3/admin/plans', {
    method: 'POST',
    body: { plan, reason: audit.reason, case_id: audit.case_id },
  });
  return res.data;
}

// ---- S34 Plan grants ----

export interface PlanGrant {
  grant_id: string;
  subject_type: string; // device | device_group | account | org
  subject_id: string;
  plan_id: string;
  plan_version: number;
  quantity: number;
  source: string; // promotion | support | partner | admin | migration
  stacking_policy: string; // additive | max | replace
  status: string; // active | revoked
  effective_from: number;
  effective_to: number; // 0 = 永久
  revoked_at?: number;
  created_at: number;
}

/** GET /v3/admin/plan-grants?subject_key= — 後端以 subject_key 為查詢鍵(DDB Query),必填。 */
export async function listPlanGrants(subjectKey: string): Promise<PlanGrant[]> {
  const res = await apiFetch<PlanGrant[]>('/v3/admin/plan-grants', { query: { subject_key: subjectKey } });
  return Array.isArray(res.data) ? res.data : [];
}

export interface CreatePlanGrantInput {
  billing_account_id?: string; // subject_type=account 可留空(後端 ensure user account)
  subject_type: string;
  subject_id: string;
  plan_id: string;
  plan_version: number;
  quantity: number;
  source: string;
  stacking_policy: string;
  effective_from?: number;
  effective_to?: number; // 省略/0 = 永久
  fair_value_minor: number;
  currency: string;
  reason: string;
  case_id: string;
}

// ---- S35 Accounting ----

export interface LedgerEntry {
  transaction_id: string;
  entry_id: string;
  billing_account_id?: string;
  account: string;
  direction: string; // debit | credit
  amount_minor: number;
  currency: string;
  occurred_at: number;
}

export interface LedgerTransaction {
  transaction_id: string;
  currency: string;
  source_type: string;
  source_id: string;
  description: string;
  occurred_at: number;
  reversal_of?: string;
  created_by: string;
  case_id?: string;
  entries: LedgerEntry[];
}

export interface Credit {
  billing_account_id: string;
  credit_id: string;
  type: string;
  amount_minor: number;
  remaining_minor: number;
  currency: string;
  status: string;
  reason: string;
  case_id: string;
  actor_user_id: string;
  ledger_transaction_id: string;
  expires_at?: number;
  created_at: number;
}

export interface Refund {
  billing_account_id: string;
  refund_id: string;
  payment_id: string;
  provider: string;
  provider_refund_id: string;
  amount_minor: number;
  currency: string;
  status: string;
  reason: string;
  case_id?: string;
  occurred_at: number;
  created_at: number;
  ledger_transaction_id: string;
}

/** GET /v3/admin/accounting/ledger-entries?account= — 以 ledger account 為查詢鍵(GSI),必填。 */
export async function listLedgerEntries(account: string): Promise<LedgerEntry[]> {
  const res = await apiFetch<LedgerEntry[]>('/v3/admin/accounting/ledger-entries', { query: { account } });
  return Array.isArray(res.data) ? res.data : [];
}

/** GET /v3/admin/accounting/ledger/{transaction_id} — 交易 + 全部分錄。 */
export async function getLedgerTransaction(transactionId: string): Promise<LedgerTransaction> {
  const res = await apiFetch<LedgerTransaction>(`/v3/admin/accounting/ledger/${encodeURIComponent(transactionId)}`);
  return { ...res.data, entries: Array.isArray(res.data?.entries) ? res.data.entries : [] };
}

export interface CreateCreditInput {
  billing_account_id: string;
  type: string;
  amount_minor: number;
  currency: string;
  reason: string;
  case_id: string;
  expires_at?: number;
}

export interface CreateRefundInput {
  billing_account_id: string;
  payment_id: string;
  amount_minor: number;
  reason: string;
  case_id: string;
}

export interface AdjustmentEntryInput {
  billing_account_id?: string;
  account: string;
  direction: string;
  amount_minor: number;
}

export interface CreateAdjustmentInput {
  currency: string;
  description: string;
  reason: string;
  case_id: string;
  entries: AdjustmentEntryInput[];
}

// ---- S36 Reconciliation ----

export interface ReconciliationRun {
  run_id: string;
  provider: string;
  period_start: number;
  period_end: number;
  status: string;
  idempotency_key: string;
  input_sha256: string;
  created_by: string;
  created_at: number;
  completed_at?: number;
  open_items: number;
}

export interface ReconciliationItem {
  provider: string;
  run_id: string;
  item_id: string;
  kind: string;
  entity_type: string;
  entity_id: string;
  currency: string;
  expected_minor: number;
  actual_minor: number;
  delta_minor: number;
  status: string; // open | investigating | resolved
  assignee_id?: string;
  resolution?: string; // adjusted | accepted | resolved
  resolution_reason?: string;
  case_id?: string;
  adjustment_transaction_id?: string;
  resolved_by?: string;
  created_at: number;
  updated_at: number;
}

export interface CreateReconciliationInput {
  provider: string;
  period_start: number;
  period_end: number;
  idempotency_key: string;
  items: Array<{
    kind: string;
    entity_type: string;
    entity_id: string;
    currency: string;
    expected_minor: number;
    actual_minor: number;
  }>;
}

/**
 * POST /v3/admin/reconciliations — 匯入一次對帳 run(差異列自動算 delta,delta=0 不入庫)。
 * 冪等:同 provider+idempotency_key+同輸入 → 回既有 run;同 key 不同輸入 → 409。
 */
export async function createReconciliation(input: CreateReconciliationInput): Promise<ReconciliationRun> {
  const res = await apiFetch<ReconciliationRun>('/v3/admin/reconciliations', { method: 'POST', body: input });
  return res.data;
}

/** GET /v3/admin/reconciliations/{run_id}/items — run 的差異清單(≤500)。 */
export async function listReconciliationItems(runId: string): Promise<ReconciliationItem[]> {
  const res = await apiFetch<ReconciliationItem[]>(`/v3/admin/reconciliations/${encodeURIComponent(runId)}/items`);
  return Array.isArray(res.data) ? res.data : [];
}

/** POST …/items/{item_id}/assign — open/investigating → investigating + 指派調查人。 */
export async function assignReconciliationItem(runId: string, itemId: string, assigneeId: string): Promise<ReconciliationItem> {
  const res = await apiFetch<ReconciliationItem>(
    `/v3/admin/reconciliations/${encodeURIComponent(runId)}/items/${encodeURIComponent(itemId)}/assign`,
    { method: 'POST', body: { assignee_id: assigneeId } }
  );
  return res.data;
}

export interface ResolveReconciliationInput {
  resolution: string; // adjusted | accepted | resolved
  reason: string;
  case_id: string;
  adjustment_transaction_id?: string; // resolution=adjusted 必填
}

/** POST …/items/{item_id}/resolve — 結案;resolution=adjusted 必附 ledger adjustment tx。 */
export async function resolveReconciliationItem(runId: string, itemId: string, input: ResolveReconciliationInput): Promise<ReconciliationItem> {
  const res = await apiFetch<ReconciliationItem>(
    `/v3/admin/reconciliations/${encodeURIComponent(runId)}/items/${encodeURIComponent(itemId)}/resolve`,
    { method: 'POST', body: input }
  );
  return res.data;
}
